import React, { FC } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ReactComponent as SuccessIcon } from '@blink/assets/src/icons/success.svg';
import { ReactComponent as PendingIcon } from '@blink/assets/src/icons/pending.svg';
import { ReactComponent as ErrorIcon } from '@blink/assets/src/icons/error.svg';
import { ReactComponent as CloseIcon } from '@blink/assets/src/icons/close.svg';

import { Store } from '@/shared/store';
import { hideModalAction, showModalAction } from '@/shared/store/modals/actions';
import { Button, ButtonVariant, DialogClose } from '@/shared/ui';
import { cn } from '@/shared/lib/tailwind-merge';

type OperationStatusModalProps = {
    type: OperationStatusType;
    description: string;
    withoutButton: boolean;
    onClick?: () => void;
};

export enum OperationStatusType {
    Success = 'Success',
    Pending = 'Pending',
    Error = 'Error',
    Cancelled = 'Cancelled',
    Timeout = 'Timeout',
}

const propsMap = new Map()
    .set(OperationStatusType.Success, {
        icon: <SuccessIcon />,
        title: 'Added Successfully',
        buttonName: 'Got it',
    })
    .set(OperationStatusType.Pending, {
        icon: <PendingIcon />,
        title: 'In Progress...',
        buttonName: 'Pending',
    })
    .set(OperationStatusType.Error, {
        icon: <ErrorIcon />,
        title: 'Oops, your request was declined...',
        buttonName: 'Try again',
    })
    .set(OperationStatusType.Cancelled, {
        icon: <ErrorIcon />,
        title: 'Transaction cancelled',
        buttonName: 'Try again',
    })
    .set(OperationStatusType.Timeout, {
        icon: <ErrorIcon />,
        title: 'Timeout',
        buttonName: 'Try again',
    });

export const OperationStatusModal: FC = () => {
    const dispatch = useDispatch();
    const { description, type, withoutButton, previousModal } = useSelector((state: Store) => ({
        ...(state.modals.modalProps as OperationStatusModalProps),
        previousModal: state.modals.previousModal,
    }));
    const { icon, title, buttonName } = propsMap.get(type) || {};

    const onClick = () => {
        if (type === OperationStatusType.Success) {
            dispatch(hideModalAction());

            return;
        }

        if (
            type === OperationStatusType.Error ||
            type === OperationStatusType.Cancelled ||
            type === OperationStatusType.Timeout
        ) {
            dispatch(
                showModalAction({
                    modalName: previousModal?.activeModal,
                    ...previousModal,
                    previousModal: null,
                }),
            );
        }
    };

    return (
        <div className='flex flex-col items-center w-[430px] text-center relative mt-5'>
            <div className='flex w-[44px] items-center gap-[10px] absolute right-0 top-[-12px]'>
                <DialogClose
                    className={cn(
                        '!mt-0 w-7 h-7 hover:opacity-80 transition-opacity',
                        icon ? '!mt-4 self-baseline' : '',
                    )}
                >
                    <CloseIcon />
                </DialogClose>
            </div>

            <div data-testid={`operation-status-${type}-icon`}>{icon}</div>
            <div
                data-testid={`operation-status-${type}-title`}
                className='text-content-primary text-[20px] font-semibold my-[24px] mt-[24px] mb-3 leading-[32px] tracking-[-0.42px]'
            >
                {title}
            </div>
            <div
                data-testid={`operation-status-${type}-description`}
                className='flex flex-col justify-center text-content-fourth mb-[28px] text-lg font-inter font-normal leading-[24px] tracking-[-0.306px]'
            >
                {description}
            </div>
            {withoutButton ? null : (
                <Button
                    data-testid={`operation-status-${type}-button`}
                    className='w-full text-lg'
                    variant={ButtonVariant.Secondary}
                    onClick={onClick}
                >
                    {buttonName}
                </Button>
            )}
        </div>
    );
};
